import { takeLatest, call, put, select } from "redux-saga/effects";
import { orderByFilterTable } from "utils/functions";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import * as Api from "./process-api";
import { Types, Creators } from "./process-duck";

export const getFilterFromStateUserSession = (state) => state.UserSession;
export const getProcessFromState = (state) => state.Process;

export const onExtractRegisterData = (registersData) =>
  registersData.items.map((item) => {
    return {
      id: item.id,
      description: item.description,
      documents: item.documents?.map((x) => x.description),
    };
  });

export const onExtractProcessData = (resp) => {
  const eventInbond = 1;
  const eventOtbound = 2;
  return {
    id: resp.id,
    description: resp.description,
    events: resp.events,
    allowImageCapture: resp.allowImageCapture,
    documents: resp.documents?.map((x) => ({
      id: x.description,
      documentInbound: x.documentEventType.includes(eventInbond) ? true : false,
      documentOutbound: x.documentEventType.includes(eventOtbound) ? true : false,
      required: x.required,
    })),
  };
};

export function* onProcessRegisterFetchAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.processShowLoadingTable());
    const process = yield select(getProcessFromState);
    const isAscending = process.filter.ascend === "ascend" ? true : false;
    const orderByFilter = orderByFilterTable(process);
    const resp = yield call(Api.getProcessRegister, {
      orderBy: orderByFilter,
      ascending: isAscending,
      _size: process.filter.itemsByPage,
      _page: process.filter.pageIndex,
      filter: process.searchFilter,
    });
    const processData = resp.data;
    const registerList = onExtractRegisterData(processData);
    yield put(Creators.processFetchRegister(registerList, processData.totalItems));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("could_not_load_processes");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.processHideLoadingTable());
  }
}

export function* onProcessSaveNewProcessAsync(action: any) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  if (action.body.events === undefined || action.body.events.length === 0) {
    const registrationErrorWithoutEvent = yield translate("necessary_to_select_a_event");
    yield YmsNotification({
      type: "error",
      description: registrationErrorWithoutEvent,
    });
  } else {
    try {
      yield put(Creators.processShowLoading());
      yield put(Creators.processDisabledRegisterButton());
      yield call(Api.saveNewProcess, action.body);
      yield put(Creators.processEnabledRegisterButton());
      yield put(Creators.processHideDrawerNewProcess());
      const registrationSuccessfullyMessage = yield translate("registration_successfully!");
      yield YmsNotification({
        type: "success",
        description: registrationSuccessfullyMessage,
      });
      yield onProcessRegisterFetchAsync();
    } catch (error) {
      console.error(error);
      yield put(Creators.processEnabledRegisterButton());
      const registrationErrorMessage = yield getError(error) ?? translate("cannot_register");
      yield YmsNotification({
        type: "error",
        description: registrationErrorMessage,
      });
    } finally {
      yield put(Creators.processHideLoading());
    }
  }
}

export function* onFetchDocumentsAsync() {
  try {
    const documentsResponse = yield call(Api.getDocuments, { actives: true });
    const documentList = documentsResponse.data;
    const document = { documentsList: documentList };
    yield put(Creators.processDocumentsList(document));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield translate("could_not_load_documents");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onProcessDeleteByIdAsync(action: any) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.processShowLoading());
    yield call(Api.deleteProcess, action.body.id);
    const registrationSuccessfullyMessage = yield translate("process_removed_with_successfully");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield onProcessRegisterFetchAsync();
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("could_not_remove_process");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.processHideLoading());
  }
}

export function* onProcessUpdateProcessAsync(action: any) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  if (action.body.events === undefined || action.body.events.length === 0) {
    const registrationErrorWithoutEvent = yield translate("necessary_to_select_a_event");
    yield YmsNotification({
      type: "error",
      description: registrationErrorWithoutEvent,
    });
  } else {
    try {
      const requestBody = { description: action.body.description, documents: action.body.documents, events: action.body.events, allowImageCapture: action.body.allowImageCapture };
      yield put(Creators.processShowLoading());
      yield call(Api.updateProcess, requestBody, action.body.id);
      yield put(Creators.processHideDrawerEditProcess());
      const registrationSuccessfullyMessage = yield translate("update_successfully");
      yield YmsNotification({
        type: "success",
        description: registrationSuccessfullyMessage,
      });
      yield onProcessRegisterFetchAsync();
    } catch (error) {
      const registrationErrorMessage = yield translate("cannot_register");
      yield YmsNotification({
        type: "error",
        description: registrationErrorMessage,
      });
    } finally {
      yield put(Creators.processHideLoading());
    }
  }
}

export function* onProcessFetchByIdAsync(action) {
  const isInbound = 1;
  const isOutbound = 2;
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.processShowLoading());
    const resp = yield call(Api.getProcessById, { id: action.id });
    const eventInboud = resp.data.events.includes(isInbound);
    const eventoutbound = resp.data.events.includes(isOutbound);
    const data = onExtractProcessData(resp.data);
    if (resp.data.documents.length > 0) {
      yield put(Creators.processUpdateVisibleDocuments(true));
    }
    yield put(Creators.processUpdateProcess(data));
    yield put(Creators.processEventInboundActive(eventInboud));
    yield put(Creators.processEventOutboundActive(eventoutbound));
    yield put(Creators.processImageCapturesActive(resp.data.allowImageCapture));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("process_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.processHideLoading());
  }
}

export function* onFetchAllProcessAsync() {
  try {
    const processResponse = yield call(Api.getAllProcess);
    const processesList = processResponse.data;
    const processes = { allProcessesList: processesList };
    yield put(Creators.processAllProcessesList(processes));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield translate("could_not_load_processes");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export default function* LocationSagas() {
  yield takeLatest(Types.PROCESS_FETCH_REGISTER_ASYNC, onProcessRegisterFetchAsync);
  yield takeLatest(Types.PROCESS_FETCH_DOCUMENTS_ASYNC, onFetchDocumentsAsync);
  yield takeLatest(Types.PROCESS_SAVE_NEW_PROCESS_ASYNC, onProcessSaveNewProcessAsync);
  yield takeLatest(Types.PROCESS_DELETE_BY_ID_ASYNC, onProcessDeleteByIdAsync);
  yield takeLatest(Types.PROCESS_UPDATE_PROCESS_ASYNC, onProcessUpdateProcessAsync);
  yield takeLatest(Types.PROCESS_FETCH_BY_ID_ASYNC, onProcessFetchByIdAsync);
  yield takeLatest(Types.PROCESS_FETCH_ALL_PROCESSES_ASYNC, onFetchAllProcessAsync);
}
